import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Download, Loader2, X } from "lucide-react";
import { MealCardsModal } from "./MealCardsModal";
import { MealHistoryModal } from "./MealHistoryModal";
import { api, formatNum } from "@/lib/api";
import { saveBlob, AY_ADLARI } from "./bankUtils";

export const MealListModal = ({ yil, ay, onClose }) => {
  const [rows, setRows] = useState(null);
  const [excl, setExcl] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [cardsOpen, setCardsOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    api.get(`/payroll/meal-list/preview?yil=${yil}&ay=${ay}`).then((r) => {
      if (!alive) return;
      setRows(r.data.rows);
      setExcl(new Set(r.data.rows.filter((x) => x.excluded).map((x) => x.personnel_id)));
    }).catch(() => { if (alive) { toast.error("Liste yüklenemedi"); onClose(); } });
    return () => { alive = false; };
  }, [yil, ay, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const included = useMemo(() => (rows || []).filter((r) => !excl.has(r.personnel_id)), [rows, excl]);
  const total = included.reduce((s, r) => s + r.toplam, 0);
  const toggle = (id) => setExcl((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payroll/meal-list", { yil, ay, excluded: [...excl] }, { responseType: "blob" });
      saveBlob(r, `pluxee_yemek_${yil}_${ay}.xlsx`);
      const nc = Number(r.headers["x-no-card"] || 0);
      if (nc) toast.warning(`Pluxee listesi indirildi ve geçmişe kaydedildi · ${nc} kişinin kartı eşleşmemiş, dosyaya girmedi`);
      else toast.success("Pluxee yükleme listesi indirildi ve geçmişe kaydedildi");
      onClose();
    } catch { toast.error("Liste oluşturulamadı"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="meal-list-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">Yemek Kartı Listesi · {AY_ADLARI[ay - 1]} {yil}</div>
            <div className="text-[11px] text-slate-500">Pluxee şablonunda çıktı · işaretini kaldırdığınız kişiler listeye girmez (bu ay için hatırlanır)</div>
          </div>
          <button onClick={() => setHistOpen(true)} data-testid="meal-history-open" className="ml-auto mr-2 inline-flex items-center gap-1 px-2.5 py-1 border border-slate-300 text-slate-700 rounded text-xs font-semibold hover:bg-slate-50">
            Geçmiş
          </button>
          <button onClick={() => setCardsOpen(true)} data-testid="meal-cards-open" className="mr-2 inline-flex items-center gap-1 px-2.5 py-1 border border-teal-600 text-teal-700 rounded text-xs font-semibold hover:bg-teal-50">
            <CreditCard size={12} /> Kartları Yönet
          </button>
          <button onClick={onClose} data-testid="meal-list-close" className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={14} /></button>
        </div>
        {!rows ? <div className="p-8 text-center text-slate-400"><Loader2 size={16} className="inline animate-spin" /></div> : (
          <>
            <div className="p-3 overflow-y-auto">
              {rows.length === 0 ? <div className="text-center text-slate-400 py-6 text-sm" data-testid="meal-list-empty">Bu ay yemek tutarı olan personel yok · Pozisyon Yemek Tutarları'ndan tutar girin</div> : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 w-6">
                        <input type="checkbox" checked={excl.size === 0} data-testid="meal-include-all"
                          onChange={() => setExcl(excl.size === 0 ? new Set(rows.map((r) => r.personnel_id)) : new Set())} />
                      </th>
                      <th className="text-left px-2 py-1.5">Ad Soyad</th><th className="text-left px-2 py-1.5">TC Kimlik No</th>
                      <th className="text-left px-2 py-1.5">Kart No</th><th className="text-right px-2 py-1.5">Gün</th>
                      <th className="text-right px-2 py-1.5">Günlük</th><th className="text-right px-2 py-1.5">Toplam</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.personnel_id} className={`border-b border-slate-100 ${excl.has(r.personnel_id) ? "opacity-50 bg-slate-50" : ""}`}>
                        <td className="px-2 py-1"><input type="checkbox" checked={!excl.has(r.personnel_id)} onChange={() => toggle(r.personnel_id)} data-testid={`meal-include-${r.sicil_no}`} /></td>
                        <td className="px-2 py-1 font-semibold">{r.ad_soyad}</td>
                        <td className="px-2 py-1 font-mono text-[11px]">{r.sicil_no}</td>
                        <td className="px-2 py-1 font-mono text-[11px]" data-testid={`meal-card-no-${r.sicil_no}`}>
                          {r.kart_no || <span className="text-rose-600 font-sans font-semibold">kart yok</span>}
                        </td>
                        <td className="px-2 py-1 text-right">{r.gun}</td>
                        <td className="px-2 py-1 text-right font-mono-tab">{formatNum(r.gunluk)}</td>
                        <td className="px-2 py-1 text-right font-mono-tab font-semibold">{formatNum(r.toplam)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-3 border-t flex items-center justify-between bg-slate-50 rounded-b-xl">
              <div className="text-xs text-slate-600" data-testid="meal-list-summary">
                {included.length} kişi · <strong>{formatNum(total)} ₺</strong>
                {included.some((r) => !r.kart_no) && <span className="text-rose-600"> · {included.filter((r) => !r.kart_no).length} kişinin kartı eşleşmemiş</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold">İptal</button>
                <button onClick={download} disabled={busy || !included.length} data-testid="meal-list-download"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Excel İndir
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {histOpen && <MealHistoryModal onClose={() => setHistOpen(false)} />}
      {cardsOpen && <MealCardsModal onClose={() => setCardsOpen(false)} onChanged={() => setTick((t) => t + 1)} />}
    </div>
  );
};
